// src/components/clients/helpers.ts
import type { ClientStatus } from "./types";
import { pipelineStatuses } from "./config";
import { parseCalendarDate } from "../../lib/calendarDate";

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

/**
 * Format a date string for display in the client sidebar + activity rows.
 *
 * The leads.move_in_timeline (and similar text fields) can hold:
 *   - ISO date strings ("2026-08-15") → formatted as "Aug 15, 2026"
 *   - free text ("August", "next month", "TBD") → returned as-is
 *   - empty / null / unparseable garbage → returned as-is (no "Invalid Date")
 *
 * Pre-existing behavior: when given unparseable input, this returned the
 * literal string "Invalid Date" from JS Date's toLocaleDateString fallback,
 * which surfaced in Zone 3 of DetailDrawer (PR #109 shipped a use of this
 * helper that hit this exact case for clients whose move_in_timeline isn't
 * a parseable date). Safer default: return the raw string so the operator
 * sees the actual stored value, or empty if there's literally nothing.
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "";
  const parsed = parseCalendarDate(dateString);
  if (Number.isNaN(parsed.getTime())) return String(dateString);
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatCompactDate = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "";
  const parsed = parseCalendarDate(dateString);
  if (Number.isNaN(parsed.getTime())) return String(dateString);
  return parsed.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
};

export const getActivityIcon = (type: string): string => {
  // PKT-0052 v1b: extend with 11 new ActivityType literals from PR #117
  // (services/activityService.ts). Mirrors that canonical Record so the
  // Timeline (this helpers map) and ActivityFeed (activityService map)
  // render the same activity_type with the same emoji. Pre-existing legacy
  // entries above the section divider keep their values — out of scope for
  // this packet. activityService.ts is the source of truth on conflict.
  const icons: Record<string, string> = {
    client_created: "👤",
    client_updated: "✏️",
    call_logged: "📞",
    spoke_on_phone: "📞",
    email_sent: "📧",
    emailed: "📧",
    sent_text: "📱", // aligned with activityService.ts (was 💬 here)
    received_text: "📩",
    left_voicemail: "📱",
    meeting_scheduled: "📅",
    note_added: "📝",
    task_created: "✅",
    task_completed: "🎉",
    tour_scheduled: "🚶",
    invoice_sent: "💵",
    payment_received: "💰",
    listing_shared: "📤",
    search_executed: "🔍",
    search_saved: "💾",
    guest_card_sent: "📋",
    lease_uploaded: "📄",
    gift_card_sent: "🎁",
    status_changed: "🔄",
    // PKT-0049 Phase 1 expansion (mirrors services/activityService.ts)
    sent_dm: "💬",
    received_dm: "💬",
    email_opened: "👁",
    email_clicked: "🔗",
    bot_drafted: "🤖",
    bot_escalated: "⚠️",
    list_shared: "📤",
    list_opened: "📬",
    interest_signal: "⭐",
  };
  return icons[type] || "📌";
};

export const getActivityColor = (type: string): string => {
  // PKT-0052 v1b: extend with 11 new ActivityType literals from PR #117.
  // Mirror services/activityService.ts canonical colors so Timeline rows
  // match other surfaces.
  const colors: Record<string, string> = {
    client_created: "#4CAF50",
    client_updated: "#2196F3",
    call_logged: "#009688",
    spoke_on_phone: "#009688",
    email_sent: "#00BCD4",
    emailed: "#00BCD4",
    sent_text: "#00BCD4", // aligned with activityService.ts (was #9C27B0 here)
    received_text: "#009688",
    left_voicemail: "#FF9800",
    meeting_scheduled: "#E91E63",
    note_added: "#795548",
    task_created: "#FF5722",
    task_completed: "#4CAF50",
    tour_scheduled: "#00BCD4",
    invoice_sent: "#FFC107",
    payment_received: "#4CAF50",
    listing_shared: "#3F51B5",
    guest_card_sent: "#673AB7",
    lease_uploaded: "#607D8B",
    gift_card_sent: "#E91E63",
    status_changed: "#2196F3",
    // PKT-0049 Phase 1 expansion (mirrors services/activityService.ts)
    sent_dm: "#00BCD4",
    received_dm: "#009688",
    email_opened: "#66BB6A",
    email_clicked: "#43A047",
    bot_drafted: "#7E57C2",
    bot_escalated: "#FF7043",
    list_shared: "#3F51B5",
    list_opened: "#66BB6A",
    interest_signal: "#FF6F00",
  };
  return colors[type] || "#757575";
};

export const getNextStatus = (
  currentStatus: ClientStatus
): ClientStatus | null => {
  const index = pipelineStatuses.indexOf(currentStatus);
  if (index >= 0 && index < pipelineStatuses.length - 1) {
    return pipelineStatuses[index + 1];
  }
  if (currentStatus === "payment_received") {
    return "closed";
  }
  return null;
};

export const formatActivityDescription = (
  type: string,
  metadata: any
): string => {
  const clientName = metadata?.client_name || "Client";
  // Notes are displayed separately below the description, so don't include them here

  switch (type) {
    case "client_created":
      return `${clientName} was added as a new client`;
    case "client_updated":
      return `${clientName}'s profile was updated`;
    case "call_logged":
    case "spoke_on_phone":
      return `Call logged${metadata?.outcome ? ` - ${metadata.outcome}` : ""}`;
    case "email_sent":

    case "invoice_status_changed":
      return `Invoice ${metadata?.invoice_number} status changed from ${metadata?.old_status} to ${metadata?.new_status}`;
    case "invoice_status_changed":
      return "📋";

    // In getActivityColor:
    case "invoice_status_changed":
      return "#3b82f6"; // blue
    case "emailed":
      return `Email sent${metadata?.subject ? `: "${metadata.subject}"` : ""}`;
    case "sent_text":
      return `Text message sent`;
    case "left_voicemail":
      return `Voicemail left`;
    case "listing_shared":
      return `${
        metadata?.property_count || metadata?.unit_count || "Properties"
      } listing(s) shared`;
    case "search_executed":
      return `Search executed (${metadata?.result_count || 0} results)`;
    case "search_saved":
      return `Search "${metadata?.search_name || "Untitled"}" saved`;
    case "note_added":
      return `Note added`;
    case "tour_scheduled":
      return `Tour scheduled${
        metadata?.property_name ? ` at ${metadata.property_name}` : ""
      }`;
    case "invoice_sent":
      return `Invoice sent`;
    case "payment_received":
      return `Payment received`;
    case "guest_card_sent":
      return `Guest card sent${
        metadata?.property_name ? ` for ${metadata.property_name}` : ""
      }`;
    case "lease_uploaded":
      return `Lease/proof of transaction uploaded`;
    case "gift_card_sent":
      return `Client gift card sent`;
    case "status_changed":
      return `Status changed to ${metadata?.new_status || "unknown"}`;
    case "task_created":
      return `Task created: ${metadata?.title || "Untitled"}`;
    case "task_completed":
      return `Task completed: ${metadata?.title || "Untitled"}`;
    // PKT-0049 Phase 1 expansion · metadata-tolerant descriptions mirroring
    // services/activityService.ts so Timeline + ActivityFeed render the
    // same activity_type with the same body copy.
    case "sent_text":
      return `Sent text to ${metadata?.client_name || "client"}${
        metadata?.body_preview
          ? `: "${String(metadata.body_preview).substring(0, 60)}"`
          : ""
      }`;
    case "received_text":
      return `Text from ${metadata?.client_name || "client"}${
        metadata?.body_preview
          ? `: "${String(metadata.body_preview).substring(0, 60)}"`
          : ""
      }`;
    case "sent_dm":
      return `Sent DM to ${
        metadata?.client_name || metadata?.sender_handle || "client"
      }`;
    case "received_dm":
      return `DM from ${
        metadata?.client_name || metadata?.sender_handle || "client"
      }`;
    case "email_opened":
      return `${metadata?.client_name || "Client"} opened email${
        metadata?.subject ? ` "${metadata.subject}"` : ""
      }`;
    case "email_clicked":
      return `${metadata?.client_name || "Client"} clicked email link${
        metadata?.subject ? ` in "${metadata.subject}"` : ""
      }`;
    case "bot_drafted":
      return `Bot drafted reply${
        metadata?.template_type ? ` (${metadata.template_type})` : ""
      }${metadata?.channel ? ` on ${metadata.channel}` : ""}`;
    case "bot_escalated":
      return `Bot escalated${
        metadata?.escalation_reason ? ` — ${metadata.escalation_reason}` : ""
      }`;
    case "list_shared":
      return `Shared list with ${metadata?.client_name || "client"}${
        metadata?.property_count
          ? ` (${metadata.property_count} properties)`
          : ""
      }`;
    case "list_opened":
      return `${metadata?.client_name || "Client"} opened the shared list`;
    case "interest_signal":
      return `${metadata?.client_name || "Client"} marked interested${
        metadata?.property_name ? ` in ${metadata.property_name}` : ""
      }`;
    default:
      return type.replace(/_/g, " ");
  }
};

export function isMobileDevice(): boolean {
  if (typeof window !== "undefined" && window.innerWidth < 600) return true;
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}
